// InstallAppButton — surfaces the browser's PWA install prompt (Chrome/Edge/
// Android/Samsung Internet). Renders nothing if the app is already installed
// or the browser doesn't fire `beforeinstallprompt`.
import React, { useEffect, useState } from 'react';
import { Download, Check } from 'lucide-react';

const T = {
  en: { install: 'Install app', installed: 'Installed' },
  me: { install: 'Instaliraj aplikaciju', installed: 'Instalirano' },
  sq: { install: 'Instalo aplikacionin', installed: 'E instaluar' },
};

const InstallAppButton = ({ lang = 'en' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const t = T[lang] || T.en;

  useEffect(() => {
    // Already-installed detection
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) { setInstalled(true); return; }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === 'accepted') setInstalled(true);
    setDeferredPrompt(null);
  };

  if (installed) {
    return (
      <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 border border-green-200 px-2.5 py-1.5 rounded text-xs font-semibold" data-testid="install-app-installed">
        <Check className="w-3.5 h-3.5" /> {t.installed}
      </span>
    );
  }
  if (!deferredPrompt) return null;

  return (
    <button
      onClick={install}
      className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-xs font-semibold shadow"
      data-testid="install-app-btn"
    >
      <Download className="w-3.5 h-3.5" /> {t.install}
    </button>
  );
};

export default InstallAppButton;
